import { PostStatus } from '../enums';

/**
 * Owner mutations on articles. Platform `articles` audience toggles gate **new creation**
 * and **published edits** only. Consumption (read, search, engagement) is unaffected.
 */
export enum ArticleOwnerMutation {
  CREATE_NEW = 'create_new',
  UPDATE_DRAFT = 'update_draft',
  UPDATE_PUBLISHED = 'update_published',
  PUBLISH_DRAFT = 'publish_draft',
  REPUBLISH = 'republish',
  DELETE = 'delete',
  READ_OWNED = 'read_owned',
  LIST_OWNED = 'list_owned',
}

export function isArticleDraftPostStatus(status: PostStatus | string): boolean {
  return status === PostStatus.DRAFT || status === PostStatus.HIDDEN;
}

export function resolveArticleOwnerMutationAccess(input: {
  articlesCreateEnabled: boolean;
  postStatus?: PostStatus | string;
  mutation: ArticleOwnerMutation;
}): { allowed: boolean; reason?: string } {
  const { articlesCreateEnabled, mutation } = input;
  const status = input.postStatus ?? PostStatus.DRAFT;
  const isDraft = isArticleDraftPostStatus(status);

  switch (mutation) {
    case ArticleOwnerMutation.CREATE_NEW:
      return articlesCreateEnabled
        ? { allowed: true }
        : {
            allowed: false,
            reason: 'Article creation is not available for your account right now.',
          };
    case ArticleOwnerMutation.UPDATE_DRAFT:
    case ArticleOwnerMutation.PUBLISH_DRAFT:
    case ArticleOwnerMutation.READ_OWNED:
    case ArticleOwnerMutation.LIST_OWNED:
    case ArticleOwnerMutation.DELETE:
      return { allowed: true };
    case ArticleOwnerMutation.UPDATE_PUBLISHED:
    case ArticleOwnerMutation.REPUBLISH:
      return articlesCreateEnabled
        ? { allowed: true }
        : {
            allowed: false,
            reason: 'Editing published articles is not available while article creation is paused.',
          };
    default:
      if (isDraft) {
        return { allowed: true };
      }
      return articlesCreateEnabled
        ? { allowed: true }
        : {
            allowed: false,
            reason: 'This article cannot be modified while article creation is paused.',
          };
  }
}

export function assertArticleOwnerMutationAccess(
  input: Parameters<typeof resolveArticleOwnerMutationAccess>[0],
): void {
  const result = resolveArticleOwnerMutationAccess(input);
  if (!result.allowed) {
    throw new Error(result.reason ?? 'Article action not allowed');
  }
}
